import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ChevronDown, Menu, X } from 'lucide-react'
import logoSecondLane from '@/assets/logoSecondLane.svg'
import openDoorIcon from '@/assets/header/open_door.svg'
import notificationIcon from '@/assets/header/notification.svg'
import { MENU_ITEMS } from '@/constants/navigation'
import { CONTAINER_SIZES } from '@/constants'
import { cn } from '@/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ThemeToggle } from '@/components/atoms'
import { NotificationsPanel } from './NotificationsPanel'
import { ContactUsModal } from './modals'
import { notificationService } from '@/services/notificationService'
import { useIsMobile } from '@/hooks/use-mobile'
import { useProfile } from '@/hooks/useProfile'

const Header = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = useIsMobile()
  const { profile } = useProfile()
  const [isNotificationsPanelOpen, setIsNotificationsPanelOpen] = useState(false)
  const [isContactUsModalOpen, setIsContactUsModalOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [unreadNotifications, setUnreadNotifications] = useState<number>(0)
  const [hoveredDropdown, setHoveredDropdown] = useState<string | null>(null)
  const [isScrolled, setIsScrolled] = useState(false)
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})

  useEffect(() => {
    loadUnreadNotificationsCount();
  }, []);

  // Close mobile menu on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false)
      }
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [isMobileMenuOpen])

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollPosition = window.scrollY;
          setIsScrolled(scrollPosition > 10);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const loadUnreadNotificationsCount = async () => {
    try {
      const notifications = await notificationService.getNotifications();
      const unreadCount = notifications.filter(n => !n.read_at).length;
      setUnreadNotifications(unreadCount);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const handleLogout = () => {
    // 🔴 Go through the /logout ROUTE, do not log out inline.
    //
    // This used to call `authService.logout()` and `navigate('/login')`
    // directly, which skipped `LogoutRoute` — and `LogoutRoute` is where
    // `queryClient.clear()` runs. Both `QueryClient`s live at module scope and
    // a client-side navigate never tears them down, so the next person to log
    // in on this machine inherited the previous user's cached responses for up
    // to the 10-minute `gcTime`. `LogoutRoute` also clears the auth store, so
    // nothing is lost by deferring to it.
    navigate('/logout');
  };

  const handleMouseEnter = (title: string) => {
    setHoveredDropdown(title);
  };

  const handleMouseLeave = (title: string, event?: React.MouseEvent) => {
    // Check if mouse actually left the dropdown area
    const dropdownElement = dropdownRefs.current[title];
    if (dropdownElement && event) {
      const rect = dropdownElement.getBoundingClientRect();
      const mouseX = event.clientX;
      const mouseY = event.clientY;
      
      // If mouse is still within dropdown bounds, don't close
      if (mouseX >= rect.left && mouseX <= rect.right && mouseY >= rect.top && mouseY <= rect.bottom) {
        return;
      }
    }
    setHoveredDropdown(null);
  };

  // Check if menu item is active
  const isMenuItemActive = (item: typeof MENU_ITEMS[0]) => {
    if (item.items) {
      // For items with dropdown, check if any sub-item path matches current location
      return item.items.some(subItem => {
        if (subItem.path === '/') {
          return location.pathname === '/'
        }
        return location.pathname.startsWith(subItem.path)
      })
    } else if (item.path) {
      // For simple items, check exact match or starts with
      if (item.path === '/') {
        return location.pathname === '/'
      }
      return location.pathname.startsWith(item.path)
    }
    return false
  };

  return (
    <>
      <header 
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-700 ease-out",
          isMobile ? "h-auto" : "h-16",
          isScrolled
            ? "bg-white/80 dark:bg-black/80 backdrop-blur-md shadow-sm"
            : "bg-transparent backdrop-blur-0"
        )}
      >
        <div className={cn("h-full", isMobile ? "px-3 py-5" : "px-6")}>
          <div className={cn(
            "h-full flex items-center justify-between",
            !isMobile && `${CONTAINER_SIZES.WIDE === '9xl' ? 'max-w-[95rem]' : 'max-w-7xl'} mx-auto`
          )}>
          {/* Logo */}
          <button
            onClick={() => navigate('/')}
            className="p-0 h-auto bg-transparent border-none cursor-pointer"
          >
            <img 
              src={logoSecondLane} 
              alt="SecondLane" 
              className={cn(
                "dark:invert",
                isMobile ? "h-8 w-32" : "h-8 w-auto"
              )}
            />
          </button>

          {/* Desktop Navigation */}
          {!isMobile && (
            <nav className="flex items-center space-x-2">
            {MENU_ITEMS.map(item => (
              <div 
                key={item.title} 
                className="relative"
                onMouseEnter={() => item.items && handleMouseEnter(item.title)}
                onMouseLeave={(e) => item.items && handleMouseLeave(item.title, e)}
                ref={(el) => {
                  if (item.items) {
                    dropdownRefs.current[item.title] = el;
                  }
                }}
              >
                {item.items ? (
                  <div>
                    <Button
                      variant="ghost"
                      className={cn(
                        "flex items-center gap-1 px-4 py-2 h-auto font-medium transition-colors rounded-full",
                        isMenuItemActive(item)
                          ? "bg-[#262626] dark:bg-white text-white dark:text-[#262626]"
                          : "text-gray-700 dark:text-[var(--header-menu-text)] hover:bg-[#262626] dark:hover:bg-white hover:text-white dark:hover:text-[#262626]"
                      )}
                    >
                      <span className="text-sm">{item.title}</span>
                      <ChevronDown size={16} className={cn("opacity-60", isMenuItemActive(item) && "text-white dark:text-[#262626]")} />
                    </Button>

                    {/* Invisible area to prevent dropdown from closing */}
                    {hoveredDropdown === item.title && (
                      <div 
                        className="absolute top-full left-0 w-56 h-1 bg-transparent"
                        onMouseEnter={() => handleMouseEnter(item.title)}
                      />
                    )}

                    {/* Custom Dropdown Menu */}
                    {hoveredDropdown === item.title && (
                      <div 
                        className="absolute top-full left-0 mt-1 w-56 bg-white dark:bg-black border border-gray-200 dark:border-gray-800 shadow-lg rounded-md overflow-hidden z-50 animate-in slide-in-from-top-2 duration-200"
                        onMouseEnter={() => handleMouseEnter(item.title)}
                        onMouseLeave={(e) => handleMouseLeave(item.title, e)}
                      >
                        {item.items.map(subItem => (
                          <button
                            key={subItem.path}
                            onClick={() => {
                              if (!subItem.isComingSoon) {
                                navigate(subItem.path)
                                setHoveredDropdown(null)
                              }
                            }}
                            disabled={subItem.isComingSoon}
                            className={cn(
                              "w-full flex items-center justify-between px-4 py-3 text-left transition-colors hover:bg-gray-100 dark:hover:bg-gray-900",
                              subItem.isComingSoon
                                ? "text-gray-400 dark:text-gray-500 cursor-not-allowed"
                                : "text-gray-700 dark:text-[var(--header-menu-text)] hover:text-black dark:hover:text-white"
                            )}
                          >
                            <span className="text-sm">{subItem.title}</span>
                            {subItem.isComingSoon && (
                              <Badge variant="secondary" className="text-xs">
                                Coming Soon
                              </Badge>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      if (item.title === 'Contact Us') {
                        setIsContactUsModalOpen(true)
                      } else {
                        navigate(item.path || '')
                      }
                    }}
                    className={cn(
                      "flex items-center gap-1 px-4 py-2 h-auto font-medium transition-colors rounded-full",
                      isMenuItemActive(item)
                        ? "bg-[#262626] dark:bg-white text-white dark:text-[#262626]"
                        : "text-gray-700 dark:text-[var(--header-menu-text)] hover:bg-[#262626] dark:hover:bg-white hover:text-white dark:hover:text-[#262626]"
                    )}
                  >
                    <span className="text-sm">{item.title}</span>
                  </Button>
                )}
              </div>
            ))}
          </nav>
          )}

          {/* Mobile Actions */}
          {isMobile ? (
            <div className="flex items-center gap-3">
              {/* User Avatar */}
              <div 
                className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 cursor-pointer"
                onClick={() => {
                  navigate('/profile')
                  setIsMobileMenuOpen(false)
                }}
              >
                {profile?.profile_picture_url ? (
                  <img src={profile.profile_picture_url} alt="User profile" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-[rgba(45,41,35,0.08)] dark:bg-[rgba(253,253,253,0.16)] flex items-center justify-center text-[10px] font-bold text-[#2d2923] dark:text-[#fdfdfd]">
                    {profile?.first_name?.[0]}{profile?.last_name?.[0] || ''}
                  </div>
                )}
              </div>
              {/* Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="bg-[rgba(255,255,255,0.8)] p-2 w-8 h-8 rounded-[16px] flex items-center justify-center"
                aria-label="Menu"
              >
                <Menu size={16} className="text-[#2d2923]" />
              </button>
            </div>
          ) : (
            /* Desktop Actions */
            <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="relative p-0 w-8 h-8 rounded-full bg-white dark:bg-[var(--header-icon-bg)] flex items-center justify-center text-gray-700 dark:text-[var(--header-menu-text)] hover:text-black dark:hover:text-white shadow-sm dark:header-icon-shadow"
              aria-label="Notifications"
              onClick={() => setIsNotificationsPanelOpen(true)}
            >
              <img src={notificationIcon} alt="Notifications" className="w-4 h-4" />
              {unreadNotifications > 0 && (
                <Badge className="absolute -top-1 -right-1 w-4 h-4 p-0 text-xs bg-red-500 text-white border-0">
                  {unreadNotifications}
                </Badge>
              )}
            </Button>
            <div className="w-8 h-8 rounded-full bg-white dark:bg-[var(--header-icon-bg)] flex items-center justify-center shadow-sm dark:header-icon-shadow">
              <ThemeToggle />
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/profile')}
              className="p-0 w-8 h-8 rounded-full bg-white dark:bg-[var(--header-avatar-bg)] flex items-center justify-center text-gray-700 dark:text-[var(--header-menu-text)] hover:text-black dark:hover:text-white shadow-sm dark:header-icon-shadow overflow-hidden"
              aria-label="Profile"
            >
              {profile?.profile_picture_url ? (
                <img src={profile.profile_picture_url} alt="User profile" className="w-full h-full object-cover rounded-full" />
              ) : (
                <div className="w-full h-full bg-[rgba(45,41,35,0.08)] dark:bg-[rgba(253,253,253,0.16)] flex items-center justify-center text-[10px] font-bold text-[#2d2923] dark:text-[#fdfdfd]">
                  {profile?.first_name?.[0]}{profile?.last_name?.[0] || ''}
                </div>
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="p-0 w-8 h-8 rounded-full bg-white dark:bg-[var(--header-icon-bg)] flex items-center justify-center text-gray-700 dark:text-[var(--header-menu-text)] hover:text-black dark:hover:text-white shadow-sm dark:header-icon-shadow"
              aria-label="Logout"
              title="Logout from system"
            >
              <img src={openDoorIcon} alt="Logout" className="w-[16px] h-[16px]" />
            </Button>
          </div>
          )}
          </div>
        </div>
      </header>

      {/* Notifications Panel */}
      <NotificationsPanel
        isOpen={isNotificationsPanelOpen}
        onClose={() => {
          setIsNotificationsPanelOpen(false);
          loadUnreadNotificationsCount();
        }}
      />

      {/* Contact Us Modal */}
      <ContactUsModal
        isOpen={isContactUsModalOpen}
        onClose={() => setIsContactUsModalOpen(false)}
        onSubmitted={() => {
          console.log('Contact Us inquiry submitted successfully')
        }}
      />

      {/* Mobile Menu */}
      {isMobile && isMobileMenuOpen && (
        <div className="fixed inset-0 z-[60] bg-[#eceaf8] dark:bg-[#25230e]">
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between px-3 py-5">
            <div className="flex items-center">
              <img 
                src={logoSecondLane} 
                alt="SecondLane" 
                className="h-8 w-32 dark:invert"
              />
            </div>
            <div className="flex items-center gap-3">
              {/* Notifications */}
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false)
                  setIsNotificationsPanelOpen(true)
                }}
                className="bg-[rgba(255,255,255,0.8)] p-2 w-8 h-8 rounded-[16px] flex items-center justify-center relative"
                aria-label="Notifications"
              >
                <img src={notificationIcon} alt="Notifications" className="w-4 h-4" />
                {unreadNotifications > 0 && (
                  <Badge className="absolute -top-1 -right-1 w-4 h-4 p-0 text-xs bg-red-500 text-white border-0">
                    {unreadNotifications}
                  </Badge>
                )}
              </button>
              {/* Theme Toggle */}
              <div className="bg-[rgba(255,255,255,0.8)] p-2 w-8 h-8 rounded-[16px] flex items-center justify-center">
                <ThemeToggle />
              </div>
              {/* User Avatar */}
              <div 
                className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 cursor-pointer"
                onClick={() => {
                  navigate('/profile')
                  setIsMobileMenuOpen(false)
                }}
              >
                {profile?.profile_picture_url ? (
                  <img src={profile.profile_picture_url} alt="User profile" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-[rgba(45,41,35,0.08)] dark:bg-[rgba(253,253,253,0.16)] flex items-center justify-center text-[10px] font-bold text-[#2d2923] dark:text-[#fdfdfd]">
                    {profile?.first_name?.[0]}{profile?.last_name?.[0] || ''}
                  </div>
                )}
              </div>
              {/* Close Button */}
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="bg-[rgba(255,255,255,0.8)] dark:bg-[rgba(255,255,255,0.8)] p-2 w-8 h-8 rounded-[16px] flex items-center justify-center"
                aria-label="Close menu"
              >
                <X size={16} className="text-[#2d2923] dark:text-[#2d2923]" />
              </button>
            </div>
          </div>

          {/* Mobile Menu Items */}
          <div className="flex flex-col gap-1.5 px-3 pt-4">
            {/* Other Menu Items */}
            {MENU_ITEMS.map((item) => {
              const hasSubItems = item.items && item.items.length > 0
              
              return (
                <button
                  key={item.title}
                  onClick={() => {
                    if (item.path) {
                      if (item.title === 'Contact Us') {
                        setIsContactUsModalOpen(true)
                      } else {
                        navigate(item.path)
                      }
                      setIsMobileMenuOpen(false)
                    } else if (hasSubItems && item.items) {
                      // Navigate to first sub-item
                      navigate(item.items[0].path)
                      setIsMobileMenuOpen(false)
                    }
                  }}
                  className="flex items-center justify-between px-4 py-2 w-full text-left text-[#35363a] dark:text-[rgba(255,255,255,0.72)] transition-colors"
                >
                  <span className="text-sm leading-5" >
                    {item.title}
                  </span>
                  {hasSubItems && (
                    <ChevronDown size={20} className="text-[#35363a] dark:text-[rgba(255,255,255,0.72)]" />
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}

export default Header 
